import { Conversation } from '../types/conversation';
import {
  LEAD_TEMPERATURE_DEFAULT_COLORS,
  LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS,
  LeadTemperatureCode,
  isLeadTemperatureCode,
  normalizeLeadTemperatureCode,
  resolveLeadTemperatureColor,
} from './leadTemperatureDisplay';

export interface ClassificationBadge {
  fieldCode: string;
  fieldTitle: string;
  slug: string;
  label: string;
  color?: string | null;
}

export interface ClassificationBadgeDisplay {
  fieldCode: string;
  fieldTitle: string;
  slug: string;
  label: string;
  color: string;
  appliedByBot?: boolean;
}

export const resolveClassificationBadgeColor = (
  badge: Pick<ClassificationBadge, 'slug' | 'color'>,
): string => {
  const trimmedColor = badge.color?.trim();
  if (trimmedColor)
  {
    return trimmedColor;
  }

  const leadCode = normalizeLeadTemperatureCode(badge.slug);
  if (leadCode)
  {
    return LEAD_TEMPERATURE_DEFAULT_COLORS[leadCode];
  }

  return '#2563eb';
};

export const resolveClassificationBadgeLabel = (
  badge: Pick<ClassificationBadge, 'slug' | 'label'>,
): string => {
  const trimmedLabel = badge.label?.trim();
  if (trimmedLabel)
  {
    return trimmedLabel;
  }

  const leadCode = normalizeLeadTemperatureCode(badge.slug);
  if (leadCode)
  {
    return LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS[leadCode];
  }

  return badge.slug;
};

export const toClassificationBadgeDisplay = (
  badge: ClassificationBadge,
  appliedByBot?: boolean,
): ClassificationBadgeDisplay => ({
  fieldCode: badge.fieldCode,
  fieldTitle: badge.fieldTitle,
  slug: badge.slug,
  label: resolveClassificationBadgeLabel(badge),
  color: resolveClassificationBadgeColor(badge),
  appliedByBot,
});

interface StoredClassificationResult {
  fields?: Record<string, string | null | undefined>;
}

const parseLeadSlugFromClassificationResult = (value?: string | null): string | null => {
  if (!value?.trim())
  {
    return null;
  }

  try
  {
    const parsed = JSON.parse(value) as StoredClassificationResult;
    const leadSlug = parsed.fields?.lead?.trim();
    return leadSlug || null;
  }
  catch
  {
    return null;
  }
};

export const findLeadClassificationBadge = (
  badges?: ReadonlyArray<ClassificationBadge> | null,
): ClassificationBadgeDisplay | null => {
  if (!badges || badges.length === 0)
  {
    return null;
  }

  const leadBadge = badges.find((badge) => badge.fieldCode === 'lead')
    ?? badges.find((badge) => isLeadTemperatureCode(badge.slug));

  if (!leadBadge)
  {
    return null;
  }

  return toClassificationBadgeDisplay(leadBadge, true);
};

export const resolveClassificationBadges = (
  conversation?: Pick<Conversation, 'classificationBadges' | 'classificationResult'> | null,
): ClassificationBadgeDisplay[] => {
  if (conversation?.classificationBadges && conversation.classificationBadges.length > 0)
  {
    return conversation.classificationBadges.map((badge) => toClassificationBadgeDisplay(badge, true));
  }

  const leadSlug = parseLeadSlugFromClassificationResult(conversation?.classificationResult);
  const leadCode = normalizeLeadTemperatureCode(leadSlug);
  if (leadCode)
  {
    return [{
      fieldCode: 'lead',
      fieldTitle: 'Temperatura do Lead',
      slug: leadCode,
      label: LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS[leadCode],
      color: LEAD_TEMPERATURE_DEFAULT_COLORS[leadCode],
      appliedByBot: true,
    }];
  }

  return [];
};

export const resolveLeadTemperatureBadge = (
  conversation?: Pick<Conversation, 'classificationBadges' | 'classificationResult'> | null,
): ClassificationBadgeDisplay | null => {
  const badges = resolveClassificationBadges(conversation);
  return findLeadClassificationBadge(badges) ?? badges[0] ?? null;
};

export type { LeadTemperatureCode };
